/** @jsx jsx */ /* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable */
/* istanbul ignore file */
import {
  Badge,
  Box,
  BoxProps,
  Button,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/core';
import { jsx } from '@emotion/core';
import i18next from 'i18next';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import moment from 'moment-timezone';

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { RiLock2Fill } from 'react-icons/ri';
import HireFromLogo from '../../../../../assets/img/Hire_Form_logo.svg';
import PixelatedImage from '../../../../../assets/img/pixelated-image.png';
import {
  Candidate,
  CandidateHireFormDetailResponse,
  CandidateHireFormDetailResponseWithError,
} from '../../../../../firebase/firestore/documents/candidate';
import { PositionType } from '../../../../../firebase/firestore/documents/position';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { PERMISSION_MODE } from '../../../../../services/permission/permission.interface';
import PermissionWrapper from '../../../../../services/permission/PermissionWrapper';
import colors from '../../../../../styles/colors';
import { getTranslatedStringValue } from '../../../../../utils/localizedNumber';
import { EmployeePermissionsEnum } from '../../../../administration/team/teamPermission/teamPermission.constant';
import { useMessagingViewContext } from '../../../MessagingViewContext';
import { HireFormReviewStatus, HireFormTypeEnum } from '../actionsDropdown/HireFormTypeEnum';
import { FieldDefinition, ResubmissionRequest } from './HireFormType';
import { HrFormRejectConfirmationModal } from './HrFormRejectConfirmationModal';
import MultipleImagePreview from './MultipleImagePreview';
import SeekerHireFormAnswers from './SeekerHireFormAnswers';
import { StatusEnum } from '../../../../dashboard/RecruiterTodoListType';
import { formatPhoneNumber, getPhoneRegion } from '../../../../../utils/PhoneUtils';
import { getConsistentDateFormat } from '../../../../../utils/dateUtils';

export interface SeekerHireFormHandle {
  loadHireDetail: (isCalledByClick: boolean) => Promise<void>;
}

export type SeekerHireFormStatusProps = BoxProps & {
  selectedCandidate: Candidate;
  isCalledFromTodoDashboard?: boolean;
  onActionDone?: () => void;
  docId?: string;
  category?: string;
};

const SeekerHireFormStatus = forwardRef<SeekerHireFormHandle, SeekerHireFormStatusProps>(
  ({ selectedCandidate, isCalledFromTodoDashboard, onActionDone, ...rest }, ref) => {
    const { t } = useTranslation('hireDetail');
    const toast = useToast();
    const {
      isHireDetailModalOpen,
      onHireDetailModalOpen,
      onHireDetailModalClose,
      currentHireFormReviewStatus,
      setCurrentHireFormReviewStatus,
    } = useMessagingViewContext();
    const {
      isOpen: isHireDetailRejectedReasonModalOpen,
      onOpen: onHireDetailRejectedReasonModalOpen,
      onClose: onHireDetailRejectedReasonModalClose,
    } = useDisclosure();
    const {
      isOpen: isTodoHireActionModalOpen,
      onOpen: onTodoHireActionModalOpen,
      onClose: onTodoHireActionModalClose,
    } = useDisclosure();

    const { isOpen: isRejectModalOpen, onOpen: onRejectModalOpen, onClose: onRejectModalClose } = useDisclosure();

    const [hireDetailOfCandidate, setHireDetailOfCandidate] = useState<CandidateHireFormDetailResponse | undefined>(undefined);
    const [supportedResubmissionFields, setSupportedResubmissionFields] = useState<FieldDefinition[]>([]);

    const [isLoadHireCalledByClick, setIsLoadHireCalledByClick] = useState(false);
    const [requestResubmission, setRequestResubmission] = useState(false);
    const hasHireFormTemplate = !!selectedCandidate?.hireFormTemplateId;

    const { loadHireDetailOfCandidate, reviewHireDetailsOfCandidate, loadResubmissionFields } = useStoreActions(
      (actions) => actions.candidate,
    );

    const [isResubmissionHrFormLoading, setIsResubmissionHrFormLoading] = useState(false);
    const [isApproveHrFormLoading, setIsApproveHrFormLoading] = useState(false);

    const { updateRecruiterActionStatus } = useStoreActions((actions) => actions.toDoList);

    const isCountryAustralia = hireDetailOfCandidate
      ? hireDetailOfCandidate?.seekerHireDetail?.business?.country === 'Australia'
      : false;

    const loadHireDetail = async (isCalledByClick: boolean): Promise<void> => {
      isCalledByClick && setIsLoadHireCalledByClick(true);

      const hireDetailResponse = await loadHireDetailOfCandidate({ candidateId: selectedCandidate.id });
      const { status, title } = hireDetailResponse as CandidateHireFormDetailResponseWithError;
      if (status) {
        toast({
          title: `${title}`,
          status: 'error',
          isClosable: true,
        });
      } else {
        setHireDetailOfCandidate(hireDetailResponse as CandidateHireFormDetailResponse);
        openHireFormModalHandler(isCalledByClick, isCalledFromTodoDashboard || false);
      }
    };

    useEffect(() => {
      if (isHireDetailModalOpen && !isLoadHireCalledByClick) {
        if (currentHireFormReviewStatus === HireFormReviewStatus.NEEDS_ACTION) {
          setRequestResubmission(true);
        }
        loadHireDetail(false);
      }

      return () => {
        setRequestResubmission(false);
        setIsLoadHireCalledByClick(false);
        setSupportedResubmissionFields([]);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHireDetailModalOpen]);

    const loadAndSetResubmisstionFields = async (hireDetailResponse: CandidateHireFormDetailResponse) => {
      let hireDetailsIdFromResponse = '';
      if (hireDetailResponse && 'seekerHireDetail' in hireDetailResponse) {
        hireDetailsIdFromResponse = hireDetailResponse.seekerHireDetail.id;
      }

      if (
        isEmpty(supportedResubmissionFields) &&
        hireDetailsIdFromResponse &&
        // inline the "isNewHireForm" logic to avoid using the identifier before it's declared:
        !(selectedCandidate.hireDetailsStatus === 'requested' || selectedCandidate.hireDetailsStatus === 'inProgress') &&
        hasHireFormTemplate
      ) {
        const resUnknown: unknown = await loadResubmissionFields({
          candidateId: selectedCandidate.id,
          hireDetailsId: hireDetailsIdFromResponse,
        });

        if (resUnknown && typeof resUnknown === 'object' && 'fields' in (resUnknown as Record<string, unknown>)) {
          const res = resUnknown as { fields?: FieldDefinition[] };
          setSupportedResubmissionFields((res.fields ?? []) as FieldDefinition[]);
        } else {
          setSupportedResubmissionFields([]);
        }
      }
    };

    useEffect(() => {
      const isReadyToShowHire = !(
        selectedCandidate.hireDetailsStatus === 'requested' || selectedCandidate.hireDetailsStatus === 'inProgress'
      );
      const isNew = isReadyToShowHire && hasHireFormTemplate;
      const hrFormCompleted = selectedCandidate.hireDetailsStatus === 'completed';

      if (isNew && hrFormCompleted && requestResubmission) {
        void loadAndSetResubmisstionFields(hireDetailOfCandidate as CandidateHireFormDetailResponse);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestResubmission, hireDetailOfCandidate]);

    const superOwners = useStoreState((s) => s.app.accounts?.superOwners);
    const isHrReviewUiEnabled = useStoreState((s) => s.app.accounts?.configuration?.hrReviewUiEnabled);
    const userId = useStoreState((s) => s.app.user?.id);

    const isSuperAdmin = (): boolean => {
      if (!superOwners || !userId) {
        return false;
      }
      return superOwners?.includes(userId);
    };

    const getColorScheme = (status: string | undefined): string => {
      switch (status) {
        case 'completed':
          return 'green';
        case 'inProgress':
          return 'teal';
        case 'approved':
          return 'cyan';
        case 'declined':
          return 'orange';
        case 'needsAction':
          return 'purple';
        case 'rejected':
          return 'red';
        case 'helpRequested':
          return 'yellow';
        default:
          return 'blue';
      }
    };

    const isReadyToShowHireDetail = (status: string | undefined): boolean => {
      return !(status === 'requested' || status === 'inProgress');
    };

    const checkResubmissionField = (field: string) => {
      const errorMsg = selectedCandidate.hireDetailsResubmissionRequest?.find((item) => item.field === field)?.comment;
      if (errorMsg) {
        return (
          <Text color="tomato" fontSize="xs">
            {errorMsg}
          </Text>
        );
      }
      return '';
    };

    const hasResubmissionField = (field: string) => {
      return !!selectedCandidate.hireDetailsResubmissionRequest?.find((item) => item.field === field);
    };

    const redactNumber = (str: string | undefined) => {
      if (!isSuperAdmin() && str) {
        const { length } = str;
        const hiddenPart = length > 3 ? '*'.repeat(length - 3) : /* istanbul ignore next */ '';
        return hiddenPart + str.substring(length - 3);
      }
      return str;
    };

    const isNewHireForm = isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) && hasHireFormTemplate;

    const validateRef = useRef<{ validate: () => boolean; getFields: () => ResubmissionRequest }>({
      validate: () => true,
      getFields: () => [],
    });

    const showResponseStatus = (status: 'info' | 'warning' | 'success' | 'error', title: string) => {
      toast({
        title: `${title}`,
        status,
        isClosable: true,
      });
    };

    const onHrFormResubmissionClick = async () => {
      const hireDetailsId = hireDetailOfCandidate?.seekerHireDetail.id;
      const candidateId = selectedCandidate.id;

      if (validateRef.current.validate()) {
        if (hireDetailOfCandidate?.seekerHireDetail.id && hireDetailsId) {
          const validData = validateRef.current.getFields();

          if (!validData?.length) {
            return toast({
              title: t('hireReview.checkAtLeastOneField'),
              status: 'error',
              isClosable: true,
            });
          }

          setIsResubmissionHrFormLoading(true);
          // call resubmission api with valid data
          const resubmissionResponse = await reviewHireDetailsOfCandidate({
            candidateId,
            hireDetailsId,
            status: HireFormReviewStatus.NEEDS_ACTION,
            resubmissionRequest: validData,
          });

          if (resubmissionResponse?.status) {
            showResponseStatus('error', resubmissionResponse?.title);
          } else {
            showResponseStatus('success', t('hireReview.hrFormResubmitSuccess'));
            if (userId && rest.docId && rest.category) {
              await updateRecruiterActionStatus({
                userId,
                docId: rest.docId,
                candidateId,
                status: StatusEnum.COMPLETED,
                category: rest.category,
                subCategory: 'hireformResubmission',
              });
            }
          }
          closeHireFormModalHandler(isCalledFromTodoDashboard || false);
        }
      } else {
        toast({
          title: t('hireReview.fillCommentsMessage'),
          status: 'error',
          isClosable: true,
        });
      }
      setIsResubmissionHrFormLoading(false);
    };

    const onHrFormApproveClick = async () => {
      setIsApproveHrFormLoading(true);
      const hireDetailsId = hireDetailOfCandidate?.seekerHireDetail.id;
      const candidateId = selectedCandidate.id;

      if (hireDetailOfCandidate?.seekerHireDetail.id && hireDetailsId) {
        const rejectionResponse = await reviewHireDetailsOfCandidate({
          candidateId,
          hireDetailsId,
          status: HireFormReviewStatus.APPROVED,
        });
        if (rejectionResponse?.status) {
          showResponseStatus('error', rejectionResponse?.title);
        } else {
          showResponseStatus('success', t('hireReview.hrFormApproveSuccess'));
          if (userId && rest.docId && rest.category) {
            await updateRecruiterActionStatus({
              userId,
              docId: rest.docId,
              candidateId,
              status: StatusEnum.COMPLETED,
              category: rest.category,
              subCategory: 'hireFormApproval',
            });
          }
        }
        closeHireFormModalHandler(isCalledFromTodoDashboard || false);
      } else {
        toast({
          title: t('common:somethingWentWrong'),
          status: 'error',
          isClosable: true,
        });
      }
      setIsApproveHrFormLoading(false);
    };

    const isHrFormCompleted = selectedCandidate.hireDetailsStatus === 'completed';
    const canShowHrFormApproveReject = isUndefined(currentHireFormReviewStatus) && !requestResubmission && isNewHireForm;

    function isValidDate(value: Date | string | null | undefined): value is Date {
      if (!value || value === 'N/A' || value === 'n/a') return false;
      // if (value instanceof Date && !isNaN(value.getTime())) return true;

      // Handle stringified dates that look like ISO strings
      const parsed = new Date(value as string);
      return !isNaN(parsed.getTime());
    }

    const openHireFormModalHandler = (isCalledByClick: boolean, isCalledFromTodoDashboard: boolean) => {
      if (isCalledFromTodoDashboard) {
        isCalledByClick && onTodoHireActionModalOpen();
      } else {
        isCalledByClick && onHireDetailModalOpen();
      }
    };

    const closeHireFormModalHandler = (isCalledFromTodoDashboard: boolean) => {
      if (isCalledFromTodoDashboard) {
        onTodoHireActionModalClose();
      } else {
        onHireDetailModalClose();
      }
    };

    useImperativeHandle(ref, () => ({
      loadHireDetail: loadHireDetail,
    }));

    return (
      <Box {...rest} mr={2}>
        <Tooltip label={t('hiringFormStatus')} hasArrow fontSize="sm">
          <PermissionWrapper
            permissions={[EmployeePermissionsEnum.HR_REVIEW]}
            mode={PERMISSION_MODE.disable}
            enableTooltip
            disable={!hasHireFormTemplate}
            noPermissionMessage="you do not have permission to view this document"
          >
            <Badge
              data-testid="loadHireDetailButton"
              colorScheme={getColorScheme(selectedCandidate.hireDetailsStatus)}
              textTransform="none"
              cursor="pointer"
              borderRadius="4px"
              variant="subtle"
              onClick={async () => {
                if (selectedCandidate.hireDetailsStatus === 'rejected') {
                  onHireDetailRejectedReasonModalOpen();
                } else {
                  await loadHireDetail(true);
                }
              }}
            >
              <Flex alignItems="center" padding="4px">
                <img src={HireFromLogo} alt="App Logo" width={22} />
                <Text ml={1} color={colors.black}>
                  {t(`status.${selectedCandidate.hireDetailsStatus}`)}
                </Text>
              </Flex>
            </Badge>
          </PermissionWrapper>
        </Tooltip>

        {hireDetailOfCandidate && (isHireDetailModalOpen || isTodoHireActionModalOpen) && (
          <Modal
            isOpen={isHireDetailModalOpen || isTodoHireActionModalOpen}
            onClose={() => {
              closeHireFormModalHandler(isCalledFromTodoDashboard || false);
              setCurrentHireFormReviewStatus(undefined);
              setRequestResubmission(false);
            }}
            size="2xl"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader px={5} paddingRight="2.1rem" borderBottom={`1px solid ${colors.gray[100]}`}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text>
                    {`${t('hireDetailInformation')} (${t(`hireFormType.${hireDetailOfCandidate.seekerHireDetail.type}`)})`}
                  </Text>
                  {isNewHireForm && isUndefined(currentHireFormReviewStatus) && isHrFormCompleted && isHrReviewUiEnabled && (
                    <PermissionWrapper
                      permissions={[EmployeePermissionsEnum.HR_REQUEST]}
                      mode={PERMISSION_MODE.hide}
                      enableTooltip
                    >
                      <Flex alignItems="center">
                        <Switch
                          id="hr-form-final-review-signature"
                          size="sm"
                          onChange={(e) => setRequestResubmission(e.target.checked)}
                          isChecked={requestResubmission}
                        />
                        <Text fontSize={14} color={colors.darkBg} fontWeight={400} ml={3} mr={3}>
                          {t('hireReview.requestResubmission')}
                        </Text>
                      </Flex>
                    </PermissionWrapper>
                  )}
                </Flex>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody p={0}>
                <Box>
                  <Box>
                    <Heading px={5} as="h3" fontSize="md">
                      {t('hiringInformation')}
                    </Heading>
                    <Box px={5} pb={1} borderTop={`1px solid ${colors.gray[50]}`} borderBottom={`1px solid ${colors.gray[50]}`}>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                          {t('positionName')}
                        </Heading>
                        <Text fontSize="sm" textAlign="right" flex="1">
                          {hireDetailOfCandidate?.seekerHireDetail.positionName}
                        </Text>
                      </Flex>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                          {t('locationName')}
                        </Heading>
                        <Text fontSize="sm" textAlign="right" flex="1">
                          {hireDetailOfCandidate?.seekerHireDetail.business.name}
                        </Text>
                      </Flex>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                          {t('jobStatus')}
                        </Heading>
                        <Text fontSize="sm" textAlign="right" flex="1">
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {isCountryAustralia && hireDetailOfCandidate?.seekerHireDetail?.positionType
                            ? {
                                [PositionType.FULL_TIME]: t(`jobPosition:full_time`),
                                [PositionType.PART_TIME]: t(`jobPosition:part_time`),
                                [PositionType.CASUAL]: t(`jobPosition:casual`),
                                [PositionType.SEASONAL]: t(`jobPosition:seasonal`),
                                [PositionType.CONTRACT]: t(`jobPosition:contract`),
                              }[hireDetailOfCandidate?.seekerHireDetail?.positionType]
                            : hireDetailOfCandidate?.seekerHireDetail.fullTime
                            ? t('fullTime')
                            : t('partTime')}
                        </Text>
                      </Flex>
                      {!hireDetailOfCandidate?.seekerHireDetail.fullTime &&
                        !isNil(hireDetailOfCandidate?.seekerHireDetail.fullTime) &&
                        hireDetailOfCandidate?.seekerHireDetail?.partTimeHours && (
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                              {t('worksHoursPerWeek')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {getTranslatedStringValue(hireDetailOfCandidate?.seekerHireDetail?.partTimeHours, i18next.language)}
                            </Text>
                          </Flex>
                        )}
                      {isValidDate(hireDetailOfCandidate?.seekerHireDetail.startingDate) && (
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('startingDate')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {getConsistentDateFormat(hireDetailOfCandidate?.seekerHireDetail.startingDate, 'full')}
                          </Text>
                        </Flex>
                      )}
                      {hireDetailOfCandidate?.seekerHireDetail.wage && hireDetailOfCandidate?.seekerHireDetail.wage?.value > 0 && (
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('wage')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {`${getTranslatedStringValue(
                              (hireDetailOfCandidate?.seekerHireDetail.wage.value / 100).toFixed(2),
                              i18next.language,
                            )} ${
                              hireDetailOfCandidate?.seekerHireDetail.wage.unit === 'custom'
                                ? `${hireDetailOfCandidate?.seekerHireDetail.wage.unit}/${hireDetailOfCandidate?.seekerHireDetail.wage.customUnit}`
                                : t(hireDetailOfCandidate?.seekerHireDetail.wage.unit)
                            }`}
                          </Text>
                        </Flex>
                      )}
                      {!isEmpty(hireDetailOfCandidate?.seekerHireDetail.note) && (
                        <Flex alignItems="flex-start" flexDirection="column" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('note')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {`${hireDetailOfCandidate?.seekerHireDetail.note ?? ''}`}
                          </Text>
                        </Flex>
                      )}
                    </Box>
                  </Box>

                  {!hasHireFormTemplate && isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) && (
                    <Box>
                      <Heading px={5} as="h3" fontSize="md">
                        {t('candidateInformation')}
                      </Heading>
                      <Box px={5} pb={1} borderTop={`1px solid ${colors.gray[50]}`} borderBottom={`1px solid ${colors.gray[50]}`}>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('firstName') ? 'tomato' : 'gray.800'}
                          >
                            {t('firstName')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.firstName}
                            {checkResubmissionField('firstName')}
                          </Text>
                        </Flex>
                        {hireDetailOfCandidate?.seekerHireDetail?.middleName && (
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('middleName') ? 'tomato' : 'gray.800'}
                            >
                              {t('middleName')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {hireDetailOfCandidate?.seekerHireDetail?.middleName}
                              {checkResubmissionField('middleName')}
                            </Text>
                          </Flex>
                        )}

                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('lastName') ? 'tomato' : 'gray.800'}
                          >
                            {t('lastName')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.lastName}
                            {checkResubmissionField('lastName')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('gender') ? 'tomato' : 'gray.800'}
                          >
                            {t('gender')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {t(hireDetailOfCandidate?.seekerHireDetail.gender)}
                            {checkResubmissionField('gender')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('dateOfBirth') ? 'tomato' : 'gray.800'}
                          >
                            {t('dateOfBirth')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.dateOfBirth
                              ? isSuperAdmin()
                                ? getConsistentDateFormat(hireDetailOfCandidate?.seekerHireDetail.dateOfBirth, 'full')
                                : moment(hireDetailOfCandidate?.seekerHireDetail.dateOfBirth).format('MMM ** YYYY')
                              : ''}
                            {checkResubmissionField('dateOfBirth')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('nickName')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.nickname}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('email') ? 'tomato' : 'gray.800'}
                          >
                            {t('email')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.email}
                            {checkResubmissionField('email')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('correspondenceLanguage') ? 'tomato' : 'gray.800'}
                          >
                            {t('correspondenceLanguage')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {t(hireDetailOfCandidate?.seekerHireDetail.correspondenceLanguage)}
                            {checkResubmissionField('correspondenceLanguage')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('phoneNumber') ? 'tomato' : 'gray.800'}
                          >
                            {t('phoneNumber')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {formatPhoneNumber(
                              hireDetailOfCandidate?.seekerHireDetail.phoneNumber,
                              getPhoneRegion(
                                hireDetailOfCandidate?.seekerHireDetail.phoneNumber,
                                selectedCandidate?.countryCode,
                              ) ||
                                selectedCandidate?.countryCode ||
                                'CA',
                              true,
                            )}
                            {checkResubmissionField('phoneNumber')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('optionalPhoneNumber') ? 'tomato' : 'gray.800'}
                          >
                            {t('optionalPhoneNumber')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {formatPhoneNumber(
                              hireDetailOfCandidate?.seekerHireDetail.optionalPhoneNumber,
                              getPhoneRegion(hireDetailOfCandidate?.seekerHireDetail.optionalPhoneNumber) ||
                                selectedCandidate?.countryCode ||
                                'CA',
                              true,
                            )}
                            {checkResubmissionField('optionalPhoneNumber')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('socialInsuranceNumber') ? 'tomato' : 'gray.800'}
                          >
                            {t('socialInsuranceNumber')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {redactNumber(hireDetailOfCandidate?.seekerHireDetail.socialInsuranceNumber)}
                            {checkResubmissionField('socialInsuranceNumber')}
                          </Text>
                        </Flex>

                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('address')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {redactNumber(hireDetailOfCandidate?.seekerHireDetail?.address?.addressLineOne)}
                          </Text>
                        </Flex>

                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('completeAddress') ? 'tomato' : 'gray.800'}
                          >
                            {t('city')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.address.city ?? ''}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('completeAddress') ? 'tomato' : 'gray.800'}
                          >
                            {t('region')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.address.region ?? ''}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('completeAddress') ? 'tomato' : 'gray.800'}
                          >
                            {t('country')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.address.country ?? ''}
                            {checkResubmissionField('completeAddress')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('postalCode') ? 'tomato' : 'gray.800'}
                          >
                            {t('postalCode')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.postalCode}
                            {checkResubmissionField('postalCode')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('apartment') ? 'tomato' : 'gray.800'}
                          >
                            {t('apartment')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.apartment}
                            {checkResubmissionField('apartment')}
                          </Text>
                        </Flex>
                      </Box>
                    </Box>
                  )}

                  {hireDetailOfCandidate.seekerHireDetail.type === HireFormTypeEnum.SEASONAL && (
                    <Box>
                      <Heading px={5} as="h3" fontSize="md">
                        {t('contract')}
                      </Heading>
                      <Box px={5} pb={1} borderTop={`1px solid ${colors.gray[50]}`} borderBottom={`1px solid ${colors.gray[50]}`}>
                        <Flex alignItems="flex-start" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('messaging:actions.hire.confirmationModal.contractStartDate')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {getConsistentDateFormat(hireDetailOfCandidate?.seekerHireDetail.startingDate, 'full')}
                          </Text>
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('messaging:actions.hire.confirmationModal.contractEndDate')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {getConsistentDateFormat(hireDetailOfCandidate?.seekerHireDetail.endDate, 'full')}
                          </Text>
                        </Flex>
                        <Box alignItems="center" justifyContent="space-between">
                          <Heading flex="1" as="h6" fontSize="sm" lineHeight="2.2">
                            {t('messaging:actions.hire.confirmationModal.contractDetailsWording')}
                          </Heading>
                          <Text fontSize="sm" textAlign="left" flex="1" whiteSpace="pre-line" lineHeight={1.3}>
                            {hireDetailOfCandidate?.seekerHireDetail.miscellaneous?.contractDetailsWordings}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {!hasHireFormTemplate && isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) && (
                    <Box>
                      <Heading px={5} as="h3" fontSize="md">
                        {t('workPermit')}
                      </Heading>
                      <Box px={5} pb={1} borderTop={`1px solid ${colors.gray[50]}`} borderBottom={`1px solid ${colors.gray[50]}`}>
                        <Flex alignItems="center" justifyContent="space-between">
                          <Heading
                            flex="1"
                            as="h6"
                            fontSize="sm"
                            lineHeight="2.2"
                            color={hasResubmissionField('workPermitEndDate') ? 'tomato' : 'gray.800'}
                          >
                            {t('messaging:actions.hire.confirmationModal.workPermitEndDate')}
                          </Heading>
                          <Text fontSize="sm" textAlign="right" flex="1">
                            {hireDetailOfCandidate?.seekerHireDetail.workPermitEndDate &&
                              getConsistentDateFormat(hireDetailOfCandidate?.seekerHireDetail.workPermitEndDate, 'full')}
                            {checkResubmissionField('workPermitEndDate')}
                          </Text>
                        </Flex>
                        {hireDetailOfCandidate?.seekerHireDetail.workPermitImageUrl && (
                          <Box w="100%" mr={2}>
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('workPermitImageUrl') ? 'tomato' : 'gray.800'}
                            >
                              {t('workPermit')}
                            </Heading>
                            {!Array.isArray(hireDetailOfCandidate?.seekerHireDetail.workPermitImageUrl) ? (
                              <Box position="relative">
                                <Image
                                  src={
                                    isSuperAdmin() ? hireDetailOfCandidate?.seekerHireDetail.workPermitImageUrl : PixelatedImage
                                  }
                                  alt="Work permit photo"
                                />
                                {!isSuperAdmin() && (
                                  <Flex
                                    border="1px solid"
                                    borderColor="gray.50"
                                    p={2}
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    alignItems="center"
                                    justifyContent="center"
                                    h="100%"
                                    w="100%"
                                  >
                                    <Text d="flex" alignItems="center">
                                      <RiLock2Fill style={{ marginRight: '5px' }} />
                                      {t('noPermissionToViewImage')}
                                    </Text>
                                  </Flex>
                                )}
                              </Box>
                            ) : (
                              <MultipleImagePreview
                                showImage={isSuperAdmin()}
                                imageURLs={hireDetailOfCandidate?.seekerHireDetail.workPermitImageUrl}
                              />
                            )}
                            {checkResubmissionField('workPermitImageUrl')}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {!hasHireFormTemplate &&
                    isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) &&
                    hireDetailOfCandidate?.seekerHireDetail.emergencyContact && (
                      <Box>
                        <Heading px={5} as="h3" fontSize="md">
                          {t('primaryContactInformation')}
                        </Heading>
                        <Box
                          px={5}
                          pb={1}
                          borderTop={`1px solid ${colors.gray[50]}`}
                          borderBottom={`1px solid ${colors.gray[50]}`}
                        >
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('emergencyContact') ? 'tomato' : 'gray.800'}
                            >
                              {t('firstName')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {hireDetailOfCandidate?.seekerHireDetail.emergencyContact.firstName}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('emergencyContact') ? 'tomato' : 'gray.800'}
                            >
                              {t('lastName')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {hireDetailOfCandidate?.seekerHireDetail.emergencyContact.lastName}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('emergencyContact') ? 'tomato' : 'gray.800'}
                            >
                              {t('phoneNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {formatPhoneNumber(
                                hireDetailOfCandidate?.seekerHireDetail.emergencyContact.phoneNumber,
                                getPhoneRegion(hireDetailOfCandidate?.seekerHireDetail.emergencyContact.phoneNumber) ||
                                  selectedCandidate?.countryCode ||
                                  'CA',
                                true,
                              )}
                            </Text>
                          </Flex>
                          {checkResubmissionField('emergencyContact')}
                        </Box>
                      </Box>
                    )}
                  {!hasHireFormTemplate &&
                    isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) &&
                    hireDetailOfCandidate?.seekerHireDetail.canadianBankAccountDetails && (
                      <Box>
                        <Heading px={5} as="h3" fontSize="md">
                          {t('canadianBankAccountDetails')}
                        </Heading>
                        <Box
                          px={5}
                          pb={1}
                          borderTop={`1px solid ${colors.gray[50]}`}
                          borderBottom={`1px solid ${colors.gray[50]}`}
                        >
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('canadianBankAccountDetails') ? 'tomato' : 'gray.800'}
                            >
                              {t('branchTransitNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {redactNumber(
                                hireDetailOfCandidate?.seekerHireDetail.canadianBankAccountDetails.branchTransitNumber,
                              )}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('canadianBankAccountDetails') ? 'tomato' : 'gray.800'}
                            >
                              {t('financialInstitutionNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {redactNumber(
                                hireDetailOfCandidate?.seekerHireDetail.canadianBankAccountDetails.financialInstitutionNumber,
                              )}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('canadianBankAccountDetails') ? 'tomato' : 'gray.800'}
                            >
                              {t('bankAccountNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {redactNumber(hireDetailOfCandidate?.seekerHireDetail.canadianBankAccountDetails.bankAccountNumber)}
                            </Text>
                          </Flex>
                          {checkResubmissionField('canadianBankAccountDetails')}
                        </Box>
                      </Box>
                    )}

                  {!hasHireFormTemplate &&
                    isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) &&
                    hireDetailOfCandidate?.seekerHireDetail.americanBankAccountDetails && (
                      <Box>
                        <Heading px={5} as="h3" fontSize="md">
                          {t('americanBankAccountDetails')}
                        </Heading>
                        <Box
                          px={5}
                          pb={1}
                          borderTop={`1px solid ${colors.gray[50]}`}
                          borderBottom={`1px solid ${colors.gray[50]}`}
                        >
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('americanBankAccountDetails') ? 'tomato' : 'gray.800'}
                            >
                              {t('routingNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {redactNumber(hireDetailOfCandidate?.seekerHireDetail.americanBankAccountDetails.routingNumber)}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" justifyContent="space-between">
                            <Heading
                              flex="1"
                              as="h6"
                              fontSize="sm"
                              lineHeight="2.2"
                              color={hasResubmissionField('americanBankAccountDetails') ? 'tomato' : 'gray.800'}
                            >
                              {t('bankAccountNumber')}
                            </Heading>
                            <Text fontSize="sm" textAlign="right" flex="1">
                              {redactNumber(hireDetailOfCandidate?.seekerHireDetail.americanBankAccountDetails.bankAccountNumber)}
                            </Text>
                          </Flex>
                          {checkResubmissionField('americanBankAccountDetails')}
                        </Box>
                      </Box>
                    )}
                  {!hasHireFormTemplate && isReadyToShowHireDetail(selectedCandidate.hireDetailsStatus) && (
                    <Box>
                      <Heading px={5} as="h3" fontSize="md">
                        {t('documents')}
                      </Heading>
                      <Box p={5} mb={2} borderTop={`1px solid ${colors.gray[50]}`} borderBottom={`1px solid ${colors.gray[50]}`}>
                        <Box alignItems="center">
                          {hireDetailOfCandidate?.seekerHireDetail.chequebookImageUrl && (
                            <Box w="100%" mr={2}>
                              <Heading
                                flex="1"
                                as="h6"
                                fontSize="sm"
                                lineHeight="2.2"
                                color={hasResubmissionField('chequebookImageUrl') ? 'tomato' : 'gray.800'}
                              >
                                {hireDetailOfCandidate?.seekerHireDetail.americanBankAccountDetails
                                  ? t('check')
                                  : t('chequeBook')}
                              </Heading>
                              {!Array.isArray(hireDetailOfCandidate?.seekerHireDetail.chequebookImageUrl) ? (
                                <Box position="relative">
                                  <Image
                                    src={
                                      isSuperAdmin() ? hireDetailOfCandidate?.seekerHireDetail.chequebookImageUrl : PixelatedImage
                                    }
                                    alt="Cheque book photo"
                                  />
                                  {!isSuperAdmin() && (
                                    <Flex
                                      border="1px solid"
                                      borderColor="gray.50"
                                      p={2}
                                      position="absolute"
                                      top={0}
                                      left={0}
                                      alignItems="center"
                                      justifyContent="center"
                                      h="100%"
                                      w="100%"
                                    >
                                      <Text d="flex" alignItems="center">
                                        <RiLock2Fill style={{ marginRight: '5px' }} />
                                        {t('noPermissionToViewImage')}
                                      </Text>
                                    </Flex>
                                  )}
                                </Box>
                              ) : (
                                <MultipleImagePreview
                                  showImage={isSuperAdmin()}
                                  imageURLs={hireDetailOfCandidate?.seekerHireDetail.chequebookImageUrl}
                                />
                              )}

                              {checkResubmissionField('chequebookImageUrl')}
                            </Box>
                          )}
                          {(hireDetailOfCandidate?.seekerHireDetail.documentIdImageUrl ||
                            checkResubmissionField('documentIdImageUrl')) && (
                            <Box w="100%" mr={2}>
                              <Heading
                                flex="1"
                                as="h6"
                                fontSize="sm"
                                lineHeight="2.2"
                                color={hasResubmissionField('documentIdImageUrl') ? 'tomato' : 'gray.800'}
                              >
                                {t('documentToImageURL')}
                              </Heading>
                              {hireDetailOfCandidate?.seekerHireDetail.documentIdImageUrl &&
                                (!Array.isArray(hireDetailOfCandidate?.seekerHireDetail.documentIdImageUrl) ? (
                                  <Box position="relative">
                                    <Image
                                      src={
                                        isSuperAdmin()
                                          ? hireDetailOfCandidate?.seekerHireDetail.documentIdImageUrl
                                          : PixelatedImage
                                      }
                                      alt="Hire Document photo"
                                    />
                                    {!isSuperAdmin() && (
                                      <Flex
                                        border="1px solid"
                                        borderColor="gray.50"
                                        p={2}
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        alignItems="center"
                                        justifyContent="center"
                                        h="100%"
                                        w="100%"
                                      >
                                        <Text d="flex" alignItems="center">
                                          <RiLock2Fill style={{ marginRight: '5px' }} />
                                          {t('noPermissionToViewImage')}
                                        </Text>
                                      </Flex>
                                    )}
                                  </Box>
                                ) : (
                                  <MultipleImagePreview
                                    showImage={isSuperAdmin()}
                                    imageURLs={hireDetailOfCandidate?.seekerHireDetail.documentIdImageUrl}
                                  />
                                ))}
                              {checkResubmissionField('documentIdImageUrl')}
                            </Box>
                          )}
                          {hireDetailOfCandidate?.seekerHireDetail.signatureImageUrl && (
                            <Box w="100%">
                              <Heading
                                flex="1"
                                as="h6"
                                fontSize="sm"
                                lineHeight="2.2"
                                color={hasResubmissionField('signatureImageUrl') ? 'tomato' : 'gray.800'}
                              >
                                {t('signature')}
                              </Heading>

                              {!Array.isArray(hireDetailOfCandidate?.seekerHireDetail.signatureImageUrl) ? (
                                <Image
                                  src={hireDetailOfCandidate?.seekerHireDetail.signatureImageUrl}
                                  alt="Signature photo"
                                  maxWidth="250px"
                                />
                              ) : (
                                <MultipleImagePreview
                                  showImage
                                  imageURLs={hireDetailOfCandidate?.seekerHireDetail.signatureImageUrl}
                                />
                              )}

                              {checkResubmissionField('signatureImageUrl')}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {isNewHireForm && (
                    <SeekerHireFormAnswers
                      selectedCandidate={selectedCandidate}
                      requestResubmission={requestResubmission}
                      registerValidation={(validate, getFields) => {
                        validateRef.current = { validate, getFields };
                      }}
                      supportedResubmissionFields={supportedResubmissionFields}
                    />
                  )}
                </Box>
              </ModalBody>
              <PermissionWrapper permissions={[EmployeePermissionsEnum.HR_REQUEST]} mode={PERMISSION_MODE.hide} enableTooltip>
                <ModalFooter px={5} position="sticky" bottom="0" backgroundColor="white" zIndex={1} borderTop="1px solid #EDF2F7">
                  {(!isHrFormCompleted || !isNewHireForm || !isHrReviewUiEnabled) && (
                    <Button
                      colorScheme="blue"
                      onClick={() => {
                        closeHireFormModalHandler(isCalledFromTodoDashboard || false);
                      }}
                    >
                      {t('close')}
                    </Button>
                  )}

                  {(currentHireFormReviewStatus === HireFormReviewStatus.REJECTED || canShowHrFormApproveReject) &&
                    isHrFormCompleted &&
                    isHrReviewUiEnabled && (
                      <Button colorScheme="red" onClick={onRejectModalOpen} mr={canShowHrFormApproveReject ? 4 : 'unset'}>
                        {t('hireReview.reject')}
                      </Button>
                    )}

                  {(currentHireFormReviewStatus === HireFormReviewStatus.APPROVED || canShowHrFormApproveReject) &&
                    isHrFormCompleted &&
                    isHrReviewUiEnabled && (
                      <Button
                        colorScheme="blue"
                        onClick={onHrFormApproveClick}
                        isDisabled={isApproveHrFormLoading}
                        isLoading={isApproveHrFormLoading}
                      >
                        {t('hireReview.approve')}
                      </Button>
                    )}

                  {(currentHireFormReviewStatus === HireFormReviewStatus.NEEDS_ACTION || requestResubmission) &&
                    isHrFormCompleted &&
                    isHrReviewUiEnabled && (
                      <Button
                        data-testid="resubmitHrFormBtn"
                        colorScheme="blue"
                        onClick={onHrFormResubmissionClick}
                        ml={4}
                        isDisabled={!requestResubmission || isResubmissionHrFormLoading}
                        isLoading={isResubmissionHrFormLoading}
                      >
                        {t('hireReview.sendForResubmission')}
                      </Button>
                    )}
                </ModalFooter>
              </PermissionWrapper>
            </ModalContent>
          </Modal>
        )}
        <HrFormRejectConfirmationModal
          isOpen={isRejectModalOpen}
          onClose={onRejectModalClose}
          hireDetailOfCandidate={hireDetailOfCandidate}
          selectedCandidate={selectedCandidate}
          onHireDetailModalClose={onHireDetailModalClose}
          onTodoHireActionModalClose={onTodoHireActionModalClose}
          docId={rest.docId}
          category={rest.category}
        />
        {isHireDetailRejectedReasonModalOpen && selectedCandidate.hireDetailsStatus === 'rejected' && (
          <Modal isOpen={isHireDetailRejectedReasonModalOpen} onClose={onHireDetailRejectedReasonModalClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('rejectedReason')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>{selectedCandidate.rejectedReason}</ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={onHireDetailRejectedReasonModalClose}>
                  {t('close')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </Box>
    );
  },
);

export default SeekerHireFormStatus;
